import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkAction } from 'redux-thunk';
import { Action } from 'redux';
import { RootState } from '../rootReducer';
import { World, Entity } from '../../systems/ecs'; // Update this import
import { WorldGrid } from '../../systems/world/WorldGrid';
import { TerrainType } from '../../types/terrain'; // Add this import
import { TERRAIN_TYPES } from '../../constants/terrain'; // Add this import
import { generateWorld } from '../../systems/generation/WorldGenerator';

// Remove the AppDispatch import
// import { AppDispatch } from '../store';

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

// Remove the Entity import if it's not used elsewhere in the file

interface SerializedEntity {
  id: string;
  components: {
    [key: string]: any;
  };
}

interface SerializedWorld {
  entities: SerializedEntity[];
  grid: {
    width: number;
    height: number;
    tiles: TerrainType[][];
  };
}

export interface LogEntry {
  message: string;
  timestamp: number;
}

export interface GameState {
  lastSaved: string | null;
  world: SerializedWorld | null;
  turn: number;
  selectedEntityId: string | null;
  resources: { [key: string]: number };
  logs: LogEntry[];
  isGeneratingWorld: boolean;
}

const initialState: GameState = {
  lastSaved: null,
  world: null,
  turn: 0,
  selectedEntityId: null,
  resources: {},
  logs: [], // Add this line
  isGeneratingWorld: false,
};

let nextEntityId = 0;

export const generateWorldThunk = createAsyncThunk(
  'game/generateWorld',
  async (_, { dispatch }) => {
    const world = generateWorld();
    return serializeWorld(world as World); // Add type assertion here
  }
);

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    initializeWorld: (state, action: PayloadAction<{ width: number; height: number }>) => {
      const { width, height } = action.payload;
      state.world = {
        entities: [],
        grid: {
          width,
          height,
          tiles: Array(height).fill(null).map(() => Array(width).fill('grass')),
        },
      };
      state.turn = 0;
    },
    addEntity: (state, action: PayloadAction<{ entityType: string; x: number; y: number }>) => {
      if (state.world) {
        const { entityType, x, y } = action.payload;
        const id = (nextEntityId++).toString();
        const serializedEntity: SerializedEntity = {
          id,
          components: {
            position: { x, y },
          },
        };

        switch (entityType) {
          case 'player':
            serializedEntity.components.movable = { speed: 2 };
            serializedEntity.components.controllable = {};
            break;
          case 'enemy':
            serializedEntity.components.movable = { speed: 1 };
            serializedEntity.components.ai = { behavior: 'random' };
            break;
        }

        state.world.entities.push(serializedEntity);
      }
    },
    selectEntity: (state, action: PayloadAction<string | null>) => {
      state.selectedEntityId = action.payload;
    },
    updateWorld: (state) => {
      console.log('updateWorld reducer called');
      state.turn += 1;
      console.log('Turn incremented:', state.turn);
    },
    updateEntityPosition: (state, action: PayloadAction<{ entityId: string; x: number; y: number }>) => {
      if (state.world) {
        const entity = state.world.entities.find(e => e.id === action.payload.entityId);
        if (entity && entity.components.position) {
          entity.components.position.x = action.payload.x;
          entity.components.position.y = action.payload.y;
        }
      }
    },
    setWorld: (state, action: PayloadAction<World>) => {
      state.world = serializeWorld(action.payload);
    },
    updateResources: (state, action: PayloadAction<{ [key: string]: number }>) => {
      Object.entries(action.payload).forEach(([resource, amount]) => {
        state.resources[resource] = (state.resources[resource] || 0) + amount;
      });
    },
    setLastSaved: (state, action: PayloadAction<string>) => {
      state.lastSaved = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateWorldThunk.pending, (state) => {
        state.isGeneratingWorld = true;
      })
      .addCase(generateWorldThunk.fulfilled, (state, action) => {
        state.isGeneratingWorld = false;
        state.world = action.payload;
      })
      .addCase(generateWorldThunk.rejected, (state) => {
        state.isGeneratingWorld = false;
        // Handle error if needed
      });
  },
});

export const { initializeWorld, addEntity, selectEntity, updateWorld, updateEntityPosition, setWorld, updateResources, setLastSaved } = gameSlice.actions;

// Add this function to serialize the world
export const serializeWorld = (world: World): SerializedWorld => {
  return {
    entities: Array.from(world.getEntities()).map(entity => ({
      id: entity.getId(),
      components: Object.fromEntries(
        Object.entries(entity.getComponents()).map(([key, value]) => [key, JSON.parse(JSON.stringify(value))])
      ),
    })),
    grid: {
      width: world.getGrid().getWidth(),
      height: world.getGrid().getHeight(),
      tiles: Array(world.getGrid().getHeight()).fill(null).map((_, y) =>
        Array(world.getGrid().getWidth()).fill(null).map((_, x) => world.getGrid().getTile(x, y))
      ),
    },
  };
};

export const deserializeWorld = (serializedWorld: SerializedWorld): World => {
  const grid = new WorldGrid(serializedWorld.grid.width, serializedWorld.grid.height, TERRAIN_TYPES[0]);
  serializedWorld.grid.tiles.forEach((row, y) => {
    row.forEach((tile, x) => {
      grid.setTile(x, y, tile);
    });
  });
  const world = new World(grid);
  serializedWorld.entities.forEach(entity => {
    const newEntity = new Entity(entity.id);
    Object.entries(entity.components).forEach(([key, value]) => {
      newEntity.addComponent(key, value);
    });
    world.addEntity(newEntity);
  });
  return world;
};

export default gameSlice.reducer;

// Helper functions to convert between serialized and ECS objects

export const saveGame = (): AppThunk => (dispatch, getState) => {
  const state = getState().game;
  localStorage.setItem('gameState', JSON.stringify(state));
};

export const loadGame = (): AppThunk => (dispatch) => {
  const savedState = localStorage.getItem('gameState');
  if (savedState) {
    dispatch(setWorld(JSON.parse(savedState).world));
  }
};

export const resetGame = (): AppThunk => (dispatch) => {
  dispatch(initializeWorld({ width: 10, height: 10 }));
};
